/**
 * Horizontal section divider. Ported from ThemeMaker's
 * {@code blocks/layout/Divider/Divider.server.tsx}.
 *
 * <p>Pure presentation; no JS shipped to the client.
 *
 * <h3>Variants</h3>
 * <ul>
 *   <li>{@code line}     — single hairline (default)</li>
 *   <li>{@code thick}    — 3px brand-color rule</li>
 *   <li>{@code dotted}   — dotted line in muted colour</li>
 *   <li>{@code gradient} — fade-in / fade-out gradient (popular editorial)</li>
 *   <li>{@code stars}    — three small ★ glyphs centred (decorative)</li>
 * </ul>
 */
import type { CSSProperties } from 'react';
import { BlockRoot } from '../_shared/BlockRoot';

const dividerStyleMap = {
  line: { borderTop: '1px solid var(--color-border-default, #d1d7dc)' },
  thick: { borderTop: '3px solid var(--color-brand-500, #3b5bdb)' },
  dotted: { borderTop: '2px dotted var(--color-text-tertiary, #6a6f73)' },
  gradient: {
    background:
      'linear-gradient(to right, transparent, var(--color-border-default, #d1d7dc), transparent)',
    height: 1,
  },
  stars: {} as CSSProperties,
} as const;

const widthMap: Record<string, string> = {
  short: '120px',
  default: '480px',
  wide: '880px',
  full: '100%',
};

const spacingMap: Record<string, string> = {
  sm: 'var(--space-4, 16px) auto',
  md: 'var(--space-8, 32px) auto',
  lg: 'var(--space-12, 48px) auto',
};

export interface DividerProps {
  variant?: keyof typeof dividerStyleMap;
  width?: keyof typeof widthMap;
  spacing?: 'sm' | 'md' | 'lg';
}

export function Divider({ variant = 'line', width = 'default', spacing = 'md' }: DividerProps) {
  const m = spacingMap[spacing] ?? spacingMap['md'];
  const w = widthMap[width] ?? widthMap['default'];

  if (variant === 'stars') {
    return (
      <BlockRoot
        as="div"
        style={{
          margin: m,
          textAlign: 'center',
          color: 'var(--color-text-tertiary, #6a6f73)',
          letterSpacing: 'var(--font-tracking-widest, 0.5em)',
          fontSize: 'var(--font-size-sm, 14px)',
        }}
      >
        ★ ★ ★
      </BlockRoot>
    );
  }

  return (
    <BlockRoot
      as="div"
      style={{
        width: w,
        margin: m,
        ...dividerStyleMap[variant],
      }}
    />
  );
}
